/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumb, Modal, Form, Select, Input, Button, Result } from 'antd';
import { css } from '@emotion/react';
import MainLayout from '../../components/common/MainLayout';
import UsersSection from '../../components/common/UsersSection';
import { createInvite, resetInvite } from '../../store/slices/inviteSlice';
import { AppDispatch } from '../../store/store';

const styles = {
  copyLinkButton: css({ marginTop: 8 }),
  inviteInstructions: css({ marginTop: 8 }),
};

const SaveUsersPage: React.FC = () => {
  const { saveId } = useParams<{ saveId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [inviteForm] = Form.useForm();
  const inviteLink = useSelector((state: any) => state.invite.link) as string | null;
  const inviteLoading = useSelector((state: any) => state.invite.loading) as boolean;

  if (!saveId) {
    return (
      <MainLayout>
        <Result status="404" title="Сейв не найден" />
      </MainLayout>
    );
  }

  const handleInviteOk = async () => {
    try {
      const values = await inviteForm.validateFields();
      await dispatch(createInvite({ saveId, roleCode: values.roleCode })).unwrap();
      inviteForm.resetFields();
    } catch (e: any) {
      if (!e?.errorFields) {
        // errors handled globally
      }
    }
  };

  const handleInviteCancel = () => {
    setInviteModalVisible(false);
    dispatch(resetInvite());
    inviteForm.resetFields();
  };

  const fullInviteLink = inviteLink ? `${window.location.protocol}//${window.location.host}/invite/${inviteLink}` : null;

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/saves">Сейвы</Link> },
            { title: <Link to={`/saves/${saveId}`}>Сейв</Link> },
            { title: 'Доступ' },
          ]}
        />
      }
    >
      <UsersSection saveId={saveId} onInviteClick={() => setInviteModalVisible(true)} />
      <Modal
        title="Пригласить пользователя"
        open={inviteModalVisible}
        onOk={() => {
          if (inviteLink) {
            handleInviteCancel();
          } else {
            handleInviteOk();
          }
        }}
        onCancel={handleInviteCancel}
        okText={inviteLink ? 'Закрыть' : 'Создать'}
        cancelText={inviteLink ? undefined : 'Отмена'}
        confirmLoading={inviteLoading}
      >
        {!inviteLink ? (
          <Form form={inviteForm} layout="vertical">
            <Form.Item label="Роль" name="roleCode" rules={[{ required: true, message: 'Выберите роль' }]}>
              <Select
                options={[
                  { value: 'manager', label: 'Менеджер' },
                  { value: 'worker', label: 'Исполнитель' },
                  { value: 'guest', label: 'Гость' },
                ]}
              />
            </Form.Item>
          </Form>
        ) : (
          <div>
            <p>Ссылка для приглашения:</p>
            <Input value={fullInviteLink || ''} readOnly />
            <Button
              css={styles.copyLinkButton}
              onClick={() => fullInviteLink && navigator.clipboard.writeText(fullInviteLink)}
            >
              Скопировать ссылку
            </Button>
            <p css={styles.inviteInstructions}>Отправьте ссылку пользователю.</p>
          </div>
        )}
      </Modal>
    </MainLayout>
  );
};

export default SaveUsersPage;